import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, WorkingStage } from 'Compass/profile-lab/services/db';
import { fetchProfileStages, fetchStageDefinition, ProfileStage } from 'Compass/profile-lab/services/ProfileLabService';
import { getUserLidOrNull } from 'Compass/profile-lab/hooks/useUserLid';
import { apiGet, apiPost } from 'Compass/profile-lab/utils/apiClient';
import { DEFAULT_MODEL } from 'Compass/profile-lab/utils/llmConstants';
import { Tab, TabBar, TabContent } from 'Compass/profile-lab/styles/selection';
import {
  ActionBar,
  ActionGroup,
  ButtonRow,
  Checkbox,
  CheckboxLabel,
  DangerButton,
  Fieldset,
  FormGroup,
  Input,
  Label,
  LoadButton,
  LoadStageContainer,
  LoadStageTitle,
  NewStageButton,
  NumberInput,
  PrimaryButton,
  SaveIndicator,
  SecondaryButton,
  Select,
  SelectGroup,
  StagePage,
  TextArea,
} from 'Compass/profile-lab/styles/stage';
import {
  ResultCloseButton,
  ResultContent,
  ResultHeader,
  ResultModal,
  ResultOverlay,
  ResultTitle,
  StageRunButton,
  StageRunStatusRow,
  StageRunStatusText,
} from 'Compass/profile-lab/styles/chat';

type TabName = 'configuration' | 'template' | 'questions' | 'combiner';

interface StageRunStatus {
  status: 'idle' | 'waiting' | 'running' | 'complete' | 'failed';
  startedAt?: string;
  llmCallCount?: number;
  elapsedSeconds?: number;
  error?: string;
  completedAt?: string;
}

interface StageStatusResponse {
  status: 'idle' | 'waiting' | 'processing' | 'completed' | 'failed';
  elapsed_seconds?: number;
  started_at?: string;
  llm_call_count?: number;
  error?: string;
}

const ProfileLabStagePage: React.FC = () => {
  const navigate = useNavigate();
  const [workingStage, setWorkingStage] = useState<WorkingStage | null>(null);
  const [availableStages, setAvailableStages] = useState<ProfileStage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabName>('configuration');
  const [isSaved, setIsSaved] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [stageRunStatus, setStageRunStatus] = useState<StageRunStatus>({ status: 'idle' });
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [resultContent, setResultContent] = useState('');
  const pollingIntervalRef = useRef<number | null>(null);
  const pollStageStatusRef = useRef<() => Promise<void>>(undefined);

  const validateExpansions = (text: string, allowedExpansions: string[]): string | null => {
    const expansionRegex = /\{([^}]+)}/g;
    let match;

    while ((match = expansionRegex.exec(text)) !== null) {
      const expansion = match[1];
      if (!allowedExpansions.includes(expansion)) {
        const displayExpansion = expansion.length > 20 ? expansion.substring(0, 20) + '...' : expansion;
        return `{${displayExpansion}}`;
      }
    }

    return null;
  };

  const validateStage = useCallback((stage: WorkingStage): string | null => {
    const enabledDatasets = stage.datasets;

    if (stage.questions) {
      const invalid = validateExpansions(stage.questions, enabledDatasets);
      if (invalid) return `Invalid expansion in questions: ${invalid}`;
    }

    const templateAllowed = [...enabledDatasets, 'questions', 'photos'];
    if (stage.template) {
      const invalid = validateExpansions(stage.template, templateAllowed);
      if (invalid) return `Invalid expansion in template: ${invalid}`;
    }

    const combinerAllowed = [...enabledDatasets, 'questions', 'photos', 'chunks'];
    if (stage.combinerTemplate) {
      const invalid = validateExpansions(stage.combinerTemplate, combinerAllowed);
      if (invalid) return `Invalid expansion in combiner: ${invalid}`;
    }

    return null;
  }, []);

  const loadWorkingStage = useCallback(async () => {
    const stage = await db.getWorkingStage();
    setWorkingStage(stage);
    if (stage) {
      setIsSaved(true);
      const error = validateStage(stage);
      setValidationError(error);
    }
  }, [validateStage]);

  const loadAvailableStages = useCallback(async () => {
    const stages = await fetchProfileStages();
    setAvailableStages(stages);
  }, []);

  const pollStageStatus = useCallback(async () => {
    try {
      const userLid = await getUserLidOrNull();
      if (!userLid) return;

      const data = await apiGet<StageStatusResponse>(`/api/dazzle/profile-lab/stage/status?user_lid=${userLid}`);

      if (data.status === 'waiting') {
        setStageRunStatus({
          status: 'waiting',
          elapsedSeconds: data.elapsed_seconds || 0
        });
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = window.setInterval(() => void pollStageStatusRef.current?.(), 2000);
        }
      } else if (data.status === 'processing') {
        setStageRunStatus({
          status: 'running',
          startedAt: data.started_at,
          llmCallCount: data.llm_call_count,
          elapsedSeconds: data.elapsed_seconds || 0
        });
        if (!pollingIntervalRef.current) {
          pollingIntervalRef.current = window.setInterval(() => void pollStageStatusRef.current?.(), 2000);
        }
      } else if (data.status === 'completed') {
        setStageRunStatus({
          status: 'complete',
          startedAt: data.started_at,
          completedAt: data.started_at,
          llmCallCount: data.llm_call_count,
          elapsedSeconds: data.elapsed_seconds || 0
        });
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (data.status === 'failed') {
        setStageRunStatus({
          status: 'failed',
          error: data.error || 'Unknown error'
        });
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (data.status === 'idle') {
        setStageRunStatus({ status: 'idle' });
      }
    } catch (error) {
      console.error('Error polling stage status:', error);
    }
  }, []);

  useEffect(() => {
    pollStageStatusRef.current = pollStageStatus;
  }, [pollStageStatus]);

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    void pollStageStatus();
    pollingIntervalRef.current = window.setInterval(() => void pollStageStatusRef.current?.(), 2000);
  };

  useEffect(() => {
    const init = async () => {
      await loadWorkingStage();
      await loadAvailableStages();
      await pollStageStatus();
    };
    void init();
  }, [loadWorkingStage, loadAvailableStages, pollStageStatus]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchResult = async () => {
    try {
      const userLid = await getUserLidOrNull();
      if (!userLid) return;

      const data = await apiGet<{ output?: string }>(`/api/dazzle/profile-lab/stage/result?user_lid=${userLid}`);
      setResultContent(data.output || 'No output available');
      setShowResultOverlay(true);
    } catch (error) {
      alert(`Error fetching result: ${error}`);
    }
  };

  const debouncedSave = useCallback((updatedStage: WorkingStage) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setIsSaved(false);

    saveTimeoutRef.current = setTimeout(async () => {
      const error = validateStage(updatedStage);
      setValidationError(error);
      await db.setWorkingStage(updatedStage);
      setIsSaved(true);
    }, 500);
  }, [validateStage]);

  const handleLoadStage = async () => {
    if (!selectedStageId) return;

    const stageDefinition = await fetchStageDefinition(selectedStageId);
    if (!stageDefinition) return;

    const newStage: Omit<WorkingStage, 'id' | 'lastModified'> = {
      stageId: stageDefinition.stage_id,
      template: stageDefinition.template,
      questions: stageDefinition.questions,
      combinerTemplate: stageDefinition.combiner_template,
      datasets: stageDefinition.datasets,
      chunkSize: stageDefinition.chunk_size,
      templateOnly: stageDefinition.template_only,
      modelType: stageDefinition.model_type,
      photoSelectionConfig: stageDefinition.photo_selection_config ? {
        maxMonths: (stageDefinition.photo_selection_config as Record<string, unknown>).max_months as number | null || null,
        limit: (stageDefinition.photo_selection_config as Record<string, unknown>).limit as number | null || null,
        onlySelfIdentity: (stageDefinition.photo_selection_config as Record<string, unknown>).only_self_identity as boolean || false,
        hasFaces: (stageDefinition.photo_selection_config as Record<string, unknown>).has_faces as boolean || false,
      } : null,
    };

    await db.setWorkingStage(newStage);
    await loadWorkingStage();
    setIsSaved(true);
  };

  const handleNewStage = async () => {
    const newStage: Omit<WorkingStage, 'id' | 'lastModified'> = {
      stageId: 'custom-stage',
      template: '',
      questions: null,
      combinerTemplate: null,
      datasets: [],
      chunkSize: 200,
      templateOnly: false,
      modelType: null,
      photoSelectionConfig: null,
    };

    await db.setWorkingStage(newStage);
    await loadWorkingStage();
    setIsSaved(true);
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    await db.clearWorkingStage();
    setWorkingStage(null);
    setShowCancelModal(false);
    setIsSaved(true);
  };

  const handleFullRun = async () => {
    if (!workingStage) return;

    if (validationError) {
      alert(`Cannot run stage with validation errors:\n${validationError}`);
      return;
    }

    try {
      const userLid = await getUserLidOrNull();

      if (!userLid) {
        alert('No user selected. Please select a user first.');
        return;
      }

      setStageRunStatus({
        status: 'waiting',
        elapsedSeconds: 0
      });

      const requestBody = {
        user_lid: userLid,
        stage_id: workingStage.stageId,
        template: workingStage.template,
        datasets: workingStage.datasets,
        questions: workingStage.questions,
        combiner_template: workingStage.combinerTemplate,
        chunk_size: workingStage.chunkSize,
        template_only: workingStage.templateOnly,
        model_type: workingStage.modelType,
        photo_selection_config: workingStage.photoSelectionConfig ? {
          max_months: workingStage.photoSelectionConfig.maxMonths,
          limit: workingStage.photoSelectionConfig.limit,
          only_self_identity: workingStage.photoSelectionConfig.onlySelfIdentity,
          has_faces: workingStage.photoSelectionConfig.hasFaces,
        } : null,
        photo_lids: null
      };

      const data = await apiPost<{ job_started?: boolean; message?: string }>('/api/dazzle/profile-lab/stage/run', requestBody);

      if (data.job_started) {
        startPolling();
      } else {
        setStageRunStatus({ status: 'idle' });
        alert(data.message);
      }
    } catch (error) {
      setStageRunStatus({ status: 'idle' });
      alert(`Error: ${error}`);
    }
  };

  const handlePreview = async () => {
    if (!workingStage) return;

    if (validationError) {
      alert(`Cannot preview stage with validation errors:\n${validationError}`);
      return;
    }

    await db.setChatConfig({ modelType: workingStage.modelType || DEFAULT_MODEL, sendHistory: false, mode: 'stage' });
    navigate('/profile-lab/chat?mode=stage&action=preview');
  };

  const handleExport = () => {
    if (!workingStage) return;

    const photoSelectionCode = workingStage.photoSelectionConfig
      ? `PhotoSelectionConfig(
        max_months=${workingStage.photoSelectionConfig.maxMonths},
        limit=${workingStage.photoSelectionConfig.limit},
        only_self_identity=${workingStage.photoSelectionConfig.onlySelfIdentity ? 'True' : 'False'},
        has_faces=${workingStage.photoSelectionConfig.hasFaces ? 'True' : 'False'},
    )`
      : 'None';

    const pythonCode = `from dazzle.agent.profile.models.profile_stages import ProfileStage
from dazzle.agent.profile.service.profile_photo_filters import PhotoSelectionConfig

ProfileStage(
    stage_id="${workingStage.stageId}",
    template="""${workingStage.template || ''}""",
    datasets=${JSON.stringify(workingStage.datasets)},
    previous_stages=[],
    photo_selection=${photoSelectionCode},
    chunk_size=${workingStage.chunkSize},
    combiner_template=${workingStage.combinerTemplate ? `"""${workingStage.combinerTemplate}"""` : 'None'},
    template_only=${workingStage.templateOnly ? 'True' : 'False'},
    questions=${workingStage.questions ? `"""${workingStage.questions}"""` : 'None'},
    model_type=${workingStage.modelType ? `"${workingStage.modelType}"` : 'None'},
)
`;

    const blob = new Blob([pythonCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workingStage.stageId}.py`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const updateStage = (updates: Partial<Omit<WorkingStage, 'id' | 'lastModified'>>) => {
    if (!workingStage) return;

    const updatedStage = { ...workingStage, ...updates };
    setWorkingStage(updatedStage);
    debouncedSave(updatedStage);
  };

  const formatElapsedTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const renderStageRunStatus = () => {
    if (stageRunStatus.status === 'idle') {
      return (
        <StageRunStatusRow $status="idle">
          <StageRunStatusText $status="idle">No stage run in progress</StageRunStatusText>
        </StageRunStatusRow>
      );
    }

    if (stageRunStatus.status === 'waiting') {
      const elapsed = formatElapsedTime(stageRunStatus.elapsedSeconds || 0);
      return (
        <StageRunStatusRow $status="waiting">
          <StageRunStatusText $status="running">
            Waiting | Elapsed: {elapsed}
          </StageRunStatusText>
        </StageRunStatusRow>
      );
    }

    if (stageRunStatus.status === 'running') {
      const elapsed = formatElapsedTime(stageRunStatus.elapsedSeconds || 0);
      return (
        <StageRunStatusRow $status="running">
          <StageRunStatusText $status="running">
            Processing | Elapsed: {elapsed} | LLM calls: {stageRunStatus.llmCallCount || 0}
          </StageRunStatusText>
        </StageRunStatusRow>
      );
    }

    if (stageRunStatus.status === 'complete') {
      return (
        <StageRunStatusRow $status="complete">
          <StageRunStatusText $status="complete">
            Complete | LLM calls: {stageRunStatus.llmCallCount || 0}
          </StageRunStatusText>
          <StageRunButton onClick={fetchResult}>Show Result</StageRunButton>
        </StageRunStatusRow>
      );
    }

    if (stageRunStatus.status === 'failed') {
      return (
        <StageRunStatusRow $status="failed">
          <StageRunStatusText $status="failed">
            Failed: {stageRunStatus.error || 'Unknown error'}
          </StageRunStatusText>
        </StageRunStatusRow>
      );
    }

    return null;
  };

  if (!workingStage) {
    return (
      <StagePage>
        <LoadStageContainer>
          <LoadStageTitle>Profile Lab Stage Editor</LoadStageTitle>
          <SelectGroup>
            <Label>Select a stage to load:</Label>
            <Select value={selectedStageId} onChange={(e) => setSelectedStageId(e.target.value)}>
              <option value="">-- Choose a stage --</option>
              {availableStages.map((stage) => (
                <option key={stage.stage_id} value={stage.stage_id}>
                  {stage.stage_id}
                </option>
              ))}
            </Select>
          </SelectGroup>
          <ButtonRow>
            <LoadButton onClick={handleLoadStage} disabled={!selectedStageId}>
              Load
            </LoadButton>
            <NewStageButton onClick={handleNewStage}>
              New Stage
            </NewStageButton>
          </ButtonRow>
        </LoadStageContainer>
      </StagePage>
    );
  }

  return (
    <StagePage>
      <ActionBar>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <SaveIndicator $saved={isSaved}>
            {isSaved ? '✓ Saved' : 'Unsaved changes'}
          </SaveIndicator>
          {validationError && (
            <SaveIndicator $saved={false}>
              ⚠ {validationError}
            </SaveIndicator>
          )}
        </div>
        <ActionGroup>
          <DangerButton onClick={handleCancel}>Cancel</DangerButton>
          <SecondaryButton onClick={handleExport}>Export</SecondaryButton>
          <SecondaryButton onClick={handlePreview}>Preview</SecondaryButton>
          <PrimaryButton onClick={handleFullRun}>Full Run</PrimaryButton>
        </ActionGroup>
      </ActionBar>

      {renderStageRunStatus()}

      <TabBar>
        <Tab $active={activeTab === 'configuration'} onClick={() => setActiveTab('configuration')}>
          Configuration
        </Tab>
        <Tab $active={activeTab === 'template'} onClick={() => setActiveTab('template')}>
          Template
        </Tab>
        <Tab $active={activeTab === 'questions'} onClick={() => setActiveTab('questions')}>
          Questions
        </Tab>
        <Tab $active={activeTab === 'combiner'} onClick={() => setActiveTab('combiner')}>
          Combiner
        </Tab>
      </TabBar>

      <TabContent $active={activeTab === 'configuration'}>
        <FormGroup>
          <Label>Stage ID</Label>
          <Input
            type="text"
            value={workingStage.stageId}
            onChange={(e) => updateStage({ stageId: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Datasets</Label>
          <CheckboxLabel>
            <Checkbox
              checked={workingStage.datasets.includes('user')}
              onChange={(e) => {
                const newDatasets = e.target.checked
                  ? [...workingStage.datasets, 'user']
                  : workingStage.datasets.filter(d => d !== 'user');
                updateStage({ datasets: newDatasets });
              }}
            />
            User
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox
              checked={workingStage.datasets.includes('visits')}
              onChange={(e) => {
                const newDatasets = e.target.checked
                  ? [...workingStage.datasets, 'visits']
                  : workingStage.datasets.filter(d => d !== 'visits');
                updateStage({ datasets: newDatasets });
              }}
            />
            Visits
          </CheckboxLabel>
        </FormGroup>

        <FormGroup>
          <Label>Model (if not default)</Label>
          <Input
            type="text"
            value={workingStage.modelType || ''}
            placeholder={`e.g., ${DEFAULT_MODEL}`}
            onChange={(e) => updateStage({ modelType: e.target.value || null })}
          />
        </FormGroup>

        <FormGroup>
          <CheckboxLabel>
            <Checkbox
              checked={workingStage.templateOnly}
              onChange={(e) => updateStage({ templateOnly: e.target.checked })}
            />
            Template only - no LLM call
          </CheckboxLabel>
        </FormGroup>

        <FormGroup>
          <Label>Chunk Size</Label>
          <NumberInput
            value={workingStage.chunkSize}
            onChange={(e) => updateStage({ chunkSize: parseInt(e.target.value) || 200 })}
          />
        </FormGroup>

        <Fieldset>
          <legend>Photo Selection</legend>
          <FormGroup>
            <Label>Max Months</Label>
            <NumberInput
              value={workingStage.photoSelectionConfig?.maxMonths || ''}
              placeholder="Leave empty for no limit"
              onChange={(e) => updateStage({
                photoSelectionConfig: {
                  ...(workingStage.photoSelectionConfig || { limit: null, onlySelfIdentity: false, hasFaces: false }),
                  maxMonths: e.target.value ? parseInt(e.target.value) : null,
                }
              })}
            />
          </FormGroup>
          <FormGroup>
            <Label>Limit</Label>
            <NumberInput
              value={workingStage.photoSelectionConfig?.limit || ''}
              placeholder="Leave empty for no limit"
              onChange={(e) => updateStage({
                photoSelectionConfig: {
                  ...(workingStage.photoSelectionConfig || { maxMonths: null, onlySelfIdentity: false, hasFaces: false }),
                  limit: e.target.value ? parseInt(e.target.value) : null,
                }
              })}
            />
          </FormGroup>
          <FormGroup>
            <CheckboxLabel>
              <Checkbox
                checked={workingStage.photoSelectionConfig?.onlySelfIdentity || false}
                onChange={(e) => updateStage({
                  photoSelectionConfig: {
                    ...(workingStage.photoSelectionConfig || { maxMonths: null, limit: null, hasFaces: false }),
                    onlySelfIdentity: e.target.checked,
                  }
                })}
              />
              Only include photos with "self"
            </CheckboxLabel>
          </FormGroup>
          <FormGroup>
            <CheckboxLabel>
              <Checkbox
                checked={workingStage.photoSelectionConfig?.hasFaces || false}
                onChange={(e) => updateStage({
                  photoSelectionConfig: {
                    ...(workingStage.photoSelectionConfig || { maxMonths: null, limit: null, onlySelfIdentity: false }),
                    hasFaces: e.target.checked,
                  }
                })}
              />
              Only include photos with faces
            </CheckboxLabel>
          </FormGroup>
        </Fieldset>
      </TabContent>

      <TabContent $active={activeTab === 'template'}>
        <FormGroup>
          <Label>Template</Label>
          <TextArea
            value={workingStage.template || ''}
            onChange={(e) => updateStage({ template: e.target.value })}
            placeholder="Enter stage template..."
          />
        </FormGroup>
      </TabContent>

      <TabContent $active={activeTab === 'questions'}>
        <FormGroup>
          <Label>Questions</Label>
          <TextArea
            value={workingStage.questions || ''}
            onChange={(e) => updateStage({ questions: e.target.value || null })}
            placeholder="Enter stage questions..."
          />
        </FormGroup>
      </TabContent>

      <TabContent $active={activeTab === 'combiner'}>
        <FormGroup>
          <Label>Combiner Template</Label>
          <TextArea
            value={workingStage.combinerTemplate || ''}
            onChange={(e) => updateStage({ combinerTemplate: e.target.value || null })}
            placeholder="Enter combiner template..."
          />
        </FormGroup>
      </TabContent>

      {showCancelModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '400px',
            }}
          >
            <h3 style={{ marginTop: 0, color: '#2d3748' }}>Cancel Stage Editing</h3>
            <p style={{ color: '#4a5568' }}>
              Are you sure you want to cancel? This will wipe the current working copy of the stage.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <SecondaryButton onClick={() => setShowCancelModal(false)}>Keep Editing</SecondaryButton>
              <DangerButton onClick={confirmCancel}>Cancel & Wipe</DangerButton>
            </div>
          </div>
        </div>
      )}

      {showResultOverlay && (
        <ResultOverlay onClick={() => setShowResultOverlay(false)}>
          <ResultModal onClick={(e) => e.stopPropagation()}>
            <ResultHeader>
              <ResultTitle>Stage Run Result</ResultTitle>
              <ResultCloseButton onClick={() => setShowResultOverlay(false)}>×</ResultCloseButton>
            </ResultHeader>
            <ResultContent>{resultContent}</ResultContent>
          </ResultModal>
        </ResultOverlay>
      )}
    </StagePage>
  );
};

export default ProfileLabStagePage;
